/**
 * useScheduleConfig — fetches + owns the Engine screen's schedule-config
 * baseline (WSRPDM-0014, contract §2.1).
 *
 * `error` is an error-KIND, not a pre-translated string — translate at the
 * call site (mirrors `useTenantRoster`'s pattern; see that hook's doc
 * comment for why `t()` must not enter this effect's dependency array).
 *
 * `applyView` lets a child (`StartStopCard`'s flag toggle, `EngineConfigForm`'s
 * knob save) adopt its PUT's re-read response as this screen's shared
 * baseline WITHOUT forcing a remount — `Engine.tsx` only mounts these
 * children once `config` is non-null, so a genuine external reload
 * (`refetch`, e.g. the load-error retry button) already unmounts/remounts
 * them for free by toggling `loading`. A save-driven `applyView` call must
 * NOT do that, or `EngineConfigForm`'s own "saved" confirmation state would
 * be wiped by the remount before the user ever saw it — each child updates
 * its OWN local form state directly from the same re-read response instead
 * (mirrors `ConfigForm`'s `setForm(toFormState(detail.config))` after save).
 */
import { useCallback, useEffect, useState } from 'react';
import { getConfig } from '@/api/wsr/scheduleConfig';
import type { PlatformScheduleConfigView } from '@/api/wsr/types';

export type ScheduleConfigLoadError = 'load-failed' | null;

export interface UseScheduleConfig {
  config: PlatformScheduleConfigView | null;
  loading: boolean;
  error: ScheduleConfigLoadError;
  refetch: () => void;
  /** Adopt a fresh effective view (a PUT's re-read response) as the shared baseline. */
  applyView: (view: PlatformScheduleConfigView) => void;
}

export function useScheduleConfig(): UseScheduleConfig {
  const [config, setConfig] = useState<PlatformScheduleConfigView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ScheduleConfigLoadError>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((view) => {
        if (cancelled) return;
        setConfig(view);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError('load-failed');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  // Set loading/error here (event-handler-style, not the effect body) ahead
  // of bumping reloadToken — mirrors useTenantRoster's refetch().
  const refetch = useCallback(() => {
    setLoading(true);
    setError(null);
    setReloadToken((n) => n + 1);
  }, []);

  const applyView = useCallback((view: PlatformScheduleConfigView) => {
    setConfig(view);
  }, []);

  return { config, loading, error, refetch, applyView };
}
